import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import apiService from '../../services/api';

const VisitorsWrapper = styled.div`
  padding: var(--spacing-6);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-6);
`;

const Title = styled.h1`
  font-size: var(--font-size-2xl);
  font-weight: 600;
  color: var(--color-secondary);
  margin: 0;
`;

const Controls = styled.div`
  display: flex;
  gap: var(--spacing-3);
  align-items: center;
`;

const Select = styled.select`
  padding: var(--spacing-2) var(--spacing-3);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius);
  background: white;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--spacing-4);
  margin-bottom: var(--spacing-6);
`;

const StatCard = styled.div`
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-4);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const StatNumber = styled.div`
  font-size: var(--font-size-2xl);
  font-weight: 700;
  color: var(--color-secondary);
  margin-bottom: var(--spacing-1);
`;

const StatLabel = styled.div`
  font-size: var(--font-size-sm);
  color: var(--color-text-muted);
  font-weight: 500;
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-6);
  margin-bottom: var(--spacing-6);
  
  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-4);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const ChartTitle = styled.h3`
  font-size: var(--font-size-lg);
  font-weight: 600;
  margin: 0 0 var(--spacing-4) 0;
  color: var(--color-secondary);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: var(--spacing-2);
  border-bottom: 2px solid var(--color-border);
  font-weight: 600;
  color: var(--color-secondary);
`;

const Td = styled.td`
  padding: var(--spacing-2);
  border-bottom: 1px solid var(--color-border);
`;

const ProgressBar = styled.div<{ percentage: number }>`
  background-color: var(--color-border);
  border-radius: var(--border-radius);
  height: 8px;
  overflow: hidden;
  margin: var(--spacing-1) 0;
  
  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${props => props.percentage}%;
    background: linear-gradient(90deg, var(--color-primary), var(--color-secondary));
    transition: width 0.3s ease;
  }
`;

const LoadingText = styled.div`
  text-align: center;
  padding: var(--spacing-8);
  color: var(--color-text-muted);
`;

const ErrorText = styled.div`
  text-align: center;
  padding: var(--spacing-8);
  color: var(--color-error);
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: var(--border-radius);
  margin-bottom: var(--spacing-4);
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 2px solid var(--color-border);
  margin-bottom: var(--spacing-6);
  background: white;
  border-radius: var(--border-radius-lg) var(--border-radius-lg) 0 0;
`;

const Tab = styled.button<{ active: boolean }>`
  padding: var(--spacing-4) var(--spacing-6);
  background: ${props => props.active ? 'white' : '#f9fafb'};
  border: none;
  border-bottom: 3px solid ${props => props.active ? 'var(--color-secondary)' : 'transparent'};
  color: ${props => props.active ? 'var(--color-secondary)' : 'var(--color-text-light)'};
  font-weight: ${props => props.active ? '600' : '400'};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: var(--font-size-sm);
  
  &:hover {
    background: white;
    color: var(--color-secondary);
  }
  
  &:first-child {
    border-radius: var(--border-radius-lg) 0 0 0;
  }
  
  &:last-child {
    border-radius: 0 var(--border-radius-lg) 0 0;
  }
`;

const BounceCard = styled.div`
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-6);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: var(--spacing-4);
`;

const BounceTitle = styled.h3`
  font-size: var(--font-size-lg);
  font-weight: 600;
  margin: 0 0 var(--spacing-4) 0;
  color: var(--color-secondary);
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
`;

const BounceMetric = styled.div`
  display: inline-block;
  background: #fef3c7;
  color: #92400e;
  padding: var(--spacing-1) var(--spacing-2);
  border-radius: var(--border-radius);
  font-weight: 600;
  font-size: var(--font-size-sm);
  margin-left: var(--spacing-2);
`;

const FunnelContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  margin: var(--spacing-4) 0;
`;

const FunnelStep = styled.div<{ percentage: number }>`
  flex: 1;
  text-align: center;
  padding: var(--spacing-3);
  background: ${props => `linear-gradient(135deg, #3b82f6 ${props.percentage}%, #e5e7eb ${props.percentage}%)`};
  color: white;
  border-radius: var(--border-radius);
  font-weight: 600;
  font-size: var(--font-size-sm);
  position: relative;
  
  &::after {
    content: '→';
    position: absolute;
    right: -15px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--color-text-light);
    font-size: var(--font-size-lg);
  }
  
  &:last-child::after {
    display: none;
  }
`;

const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('sv-SE').format(Math.round(num));
};

const formatDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  if (minutes === 0) return `${remainingSeconds}s`;
  return `${minutes}m ${remainingSeconds}s`;
};

const formatPrice = (price: number): string => {
  return new Intl.NumberFormat('sv-SE', { 
    style: 'currency', 
    currency: 'SEK' 
  }).format(price);
};

const Visitors: React.FC = () => {
  const [period, setPeriod] = useState<number>(30);
  const [activeTab, setActiveTab] = useState<'overview' | 'products' | 'bounce' | 'marketing'>('overview');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [stats, setStats] = useState<any>(null);
  const [productStats, setProductStats] = useState<any>(null);
  const [bounceStats, setBounceStats] = useState<any>(null);
  const [marketingStats, setMarketingStats] = useState<any>(null);

  const fetchStatistics = async (days: number) => {
    setLoading(true);
    setError('');
    
    try {
      const data = await apiService.getVisitorStatistics(days);
      setStats(data);
    } catch (err: any) {
      setError(`Kunde inte hämta besöksstatistik: ${err.message}`);
      console.error('Error fetching visitor statistics:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProductStatistics = async (days: number) => {
    setLoading(true);
    setError('');

    try {
      const data = await apiService.getMostVisitedProducts(50, days);
      setProductStats(data);
    } catch (err: any) {
      setError(`Kunde inte hämta produktstatistik: ${err.message}`);
      console.error('Error fetching product statistics:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBounceAnalytics = async (days: number) => {
    setLoading(true);
    setError('');
    
    try {
      const data = await apiService.getBounceAnalytics(days);
      setBounceStats(data);
    } catch (err: any) {
      setError(`Kunde inte hämta bounce-statistik: ${err.message}`);
      console.error('Error fetching bounce analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMarketingStatistics = async (days: number) => {
    setLoading(true);
    setError('');

    try {
      const data = await apiService.getMarketingPageStats(days);
      setMarketingStats(data);
    } catch (err: any) {
      setError(`Kunde inte hämta statistik för marknadsföringssidor: ${err.message}`);
      console.error('Error fetching marketing page statistics:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'overview') {
      fetchStatistics(period);
    } else if (activeTab === 'products') {
      fetchProductStatistics(period);
    } else if (activeTab === 'bounce') {
      fetchBounceAnalytics(period);
    } else if (activeTab === 'marketing') {
      fetchMarketingStatistics(period);
    }
  }, [period, activeTab]);

  const handleRetry = () => {
    if (activeTab === 'overview') {
      fetchStatistics(period);
    } else if (activeTab === 'products') {
      fetchProductStatistics(period);
    } else if (activeTab === 'bounce') {
      fetchBounceAnalytics(period);
    } else if (activeTab === 'marketing') {
      fetchMarketingStatistics(period);
    }
  };

  if (loading) {
    return (
      <VisitorsWrapper>
        <Title>Besökare</Title>
        <LoadingText>Laddar besöksstatistik...</LoadingText>
      </VisitorsWrapper>
    );
  }

  if (error) {
    return (
      <VisitorsWrapper>
        <Title>Besökare</Title>
        <ErrorText>{error}</ErrorText>
        <button onClick={handleRetry} style={{ padding: '8px 16px' }}>
          Försök igen
        </button>
      </VisitorsWrapper>
    );
  }

  const overview = stats?.statistics?.overview;
  const dailyViews = stats?.statistics?.daily_views || [];
  const topProducts = stats?.top_products || [];
  const topPages = stats?.top_pages || [];
  const deviceStats = stats?.device_stats;
  const referrerStats = stats?.referrer_stats || [];
  const conversionStats = stats?.conversion_stats || [];
  
  const bounceAnalytics = bounceStats?.bounce_analytics;
  const productData = productStats?.products || [];
  const productPeriodDays = productStats?.period_days;
  const totalProductViews = productData.reduce((sum: number, product: any) => sum + (product.view_count || 0), 0);
  const totalProductVisitors = productData.reduce((sum: number, product: any) => sum + (product.unique_visitors || 0), 0);
  const averageProductViews = productData.length > 0 ? totalProductViews / productData.length : 0;
  const topProduct = productData[0];
  const marketingData = marketingStats?.pages || [];
  const marketingTotals = marketingStats?.totals || {};
  const marketingPeriodDays = marketingStats?.period_days ?? period;
  const totalMarketingViews = marketingTotals?.total_views ?? 0;
  const totalMarketingVisitors = marketingTotals?.unique_visitors ?? 0;
  const avgMarketingTime = marketingTotals?.avg_time_on_page ?? 0;
  const topMarketingPage = marketingData[0];

  return (
    <VisitorsWrapper>
      <Header>
        <Title>📊 Besökare</Title>
        <Controls>
          <span>Tidsperiod:</span>
          <Select value={period} onChange={(e) => setPeriod(Number(e.target.value))}>
            <option value={7}>Senaste 7 dagarna</option>
            <option value={30}>Senaste 30 dagarna</option>
            <option value={90}>Senaste 90 dagarna</option>
            <option value={365}>Senaste året</option>
          </Select>
        </Controls>
      </Header>

      <TabContainer>
        <Tab 
          active={activeTab === 'overview'} 
          onClick={() => setActiveTab('overview')}
        >
          📊 Översikt
        </Tab>
        <Tab 
          active={activeTab === 'products'} 
          onClick={() => setActiveTab('products')}
        >
          📦 Produkter
        </Tab>
        <Tab 
          active={activeTab === 'bounce'} 
          onClick={() => setActiveTab('bounce')}
        >
          📈 Bounce-analys
        </Tab>
        <Tab
          active={activeTab === 'marketing'}
          onClick={() => setActiveTab('marketing')}
        >
          📣 Marknadsföringssidor
        </Tab>
      </TabContainer>

      {activeTab === 'overview' && (
        <>
          {/* Overview Statistics */}
          <StatsGrid>
        <StatCard>
          <StatNumber>{formatNumber(overview?.total_visitors || 0)}</StatNumber>
          <StatLabel>Unika besökare</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{formatNumber(overview?.total_sessions || 0)}</StatNumber>
          <StatLabel>Totala sessioner</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{formatNumber(overview?.avg_pages_per_session || 0)}</StatNumber>
          <StatLabel>Sidor per session</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{formatDuration(overview?.avg_session_duration || 0)}</StatNumber>
          <StatLabel>Genomsnittlig sessionstid</StatLabel>
        </StatCard>
      </StatsGrid>

      <ChartsGrid>
        {/* Most Visited Products */}
        <ChartCard>
          <ChartTitle>🏆 Mest besökta produkter</ChartTitle>
          {topProducts.length > 0 ? (
            <Table>
              <thead>
                <tr>
                  <Th>Produkt</Th>
                  <Th>Visningar</Th>
                  <Th>Unika</Th>
                  <Th>Pris</Th>
                </tr>
              </thead>
              <tbody>
                {topProducts.slice(0, 10).map((product: any, index: number) => (
                  <tr key={product.id}>
                    <Td>
                      <strong>{product.name}</strong>
                    </Td>
                    <Td>{formatNumber(product.view_count)}</Td>
                    <Td>{formatNumber(product.unique_visitors)}</Td>
                    <Td>{formatPrice(product.price)}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
              Inga produktvisningar ännu
            </div>
          )}
        </ChartCard>

        {/* Most Visited Pages */}
        <ChartCard>
          <ChartTitle>📄 Mest besökta sidor</ChartTitle>
          {topPages.length > 0 ? (
            <Table>
              <thead>
                <tr>
                  <Th>Sida</Th>
                  <Th>Visningar</Th>
                  <Th>Tid på sida</Th>
                </tr>
              </thead>
              <tbody>
                {topPages.slice(0, 8).map((page: any, index: number) => (
                  <tr key={index}>
                    <Td>
                      <div>
                        <strong>{page.page_title || page.page_url}</strong>
                        <div style={{ fontSize: '0.8em', color: '#666' }}>
                          {page.page_type}
                        </div>
                      </div>
                    </Td>
                    <Td>{formatNumber(page.view_count)}</Td>
                    <Td>{formatDuration(page.avg_time_on_page || 0)}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
              Inga sidvisningar ännu
            </div>
          )}
        </ChartCard>

        {/* Device Statistics */}
        <ChartCard>
          <ChartTitle>📱 Enhetsstatistik</ChartTitle>
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Enhetstyper</h4>
            {deviceStats?.devices?.map((device: any) => (
              <div key={device.device_type} style={{ marginBottom: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ textTransform: 'capitalize' }}>{device.device_type}</span>
                  <span>{device.percentage}%</span>
                </div>
                <ProgressBar percentage={device.percentage} />
              </div>
            ))}
          </div>
          
          <div>
            <h4 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Webbläsare</h4>
            {deviceStats?.browsers?.slice(0, 5).map((browser: any) => (
              <div key={browser.browser} style={{ marginBottom: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span>{browser.browser}</span>
                  <span>{browser.percentage}%</span>
                </div>
                <ProgressBar percentage={browser.percentage} />
              </div>
            ))}
          </div>
        </ChartCard>

        {/* Referrer Statistics */}
        <ChartCard>
          <ChartTitle>🔗 Trafikkällor</ChartTitle>
          {referrerStats.map((referrer: any) => (
            <div key={referrer.referrer_source} style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span>
                  {referrer.referrer_source === 'Direct' && '🔗 Direktbesök'}
                  {referrer.referrer_source === 'Google' && '🔍 Google'}
                  {referrer.referrer_source === 'Facebook' && '📘 Facebook'}
                  {referrer.referrer_source === 'Instagram' && '📷 Instagram'}
                  {referrer.referrer_source === 'Other' && '🌐 Andra'}
                  {!['Direct', 'Google', 'Facebook', 'Instagram', 'Other'].includes(referrer.referrer_source) && referrer.referrer_source}
                </span>
                <span>{formatNumber(referrer.count)} ({referrer.percentage}%)</span>
              </div>
              <ProgressBar percentage={referrer.percentage} />
            </div>
          ))}
        </ChartCard>
      </ChartsGrid>

      {/* Conversion Statistics */}
      {conversionStats.length > 0 && (
        <ChartCard style={{ marginBottom: '20px' }}>
          <ChartTitle>🎯 Konverteringar</ChartTitle>
          <Table>
            <thead>
              <tr>
                <Th>Händelse</Th>
                <Th>Antal</Th>
                <Th>Unika sessioner</Th>
              </tr>
            </thead>
            <tbody>
              {conversionStats.map((conversion: any) => (
                <tr key={conversion.event_type}>
                  <Td>
                    {conversion.event_type === 'add_to_cart' && '🛒 Lägg i kundvagn'}
                    {conversion.event_type === 'purchase' && '💳 Köp'}
                    {conversion.event_type === 'form_submit' && '📝 Formulär skickat'}
                    {!['add_to_cart', 'purchase', 'form_submit'].includes(conversion.event_type) && conversion.event_type}
                  </Td>
                  <Td>{formatNumber(conversion.count)}</Td>
                  <Td>{formatNumber(conversion.unique_sessions)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </ChartCard>
      )}

      {/* Daily Views Chart */}
      <ChartCard>
        <ChartTitle>📈 Daglig trafik ({period} dagar) - {dailyViews.length} dagar med data</ChartTitle>
        {dailyViews.length > 0 ? (
          <div style={{ 
            display: 'flex', 
            alignItems: 'end', 
            gap: '2px', 
            height: '200px', 
            padding: '20px 0',
            overflowX: 'auto'
          }}>
            {dailyViews.map((day: any, index: number) => {
              const maxViews = Math.max(...dailyViews.map((d: any) => d.views));
              const height = maxViews > 0 ? (day.views / maxViews) * 150 : 2;
              
              return (
                <div key={`${day.date}-${index}`} style={{ 
                  minWidth: dailyViews.length > 30 ? '20px' : 'auto',
                  flex: dailyViews.length <= 30 ? 1 : 'none',
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  position: 'relative'
                }}>
                  <div style={{ 
                    fontSize: '10px', 
                    marginBottom: '5px', 
                    textAlign: 'center',
                    fontWeight: '500',
                    color: 'var(--color-text)'
                  }}>
                    {day.views}
                  </div>
                  <div style={{
                    backgroundColor: '#8B5CF6',
                    height: `${Math.max(height, 2)}px`,
                    width: dailyViews.length > 30 ? '16px' : '100%',
                    borderRadius: '2px 2px 0 0',
                    minHeight: '2px',
                    transition: 'height 0.3s ease',
                    cursor: 'pointer'
                  }}
                  title={`${day.date}: ${day.views} visningar`}
                  />
                  <div style={{ 
                    fontSize: dailyViews.length > 30 ? '8px' : '10px',
                    marginTop: '5px', 
                    transform: 'rotate(-45deg)',
                    transformOrigin: 'center',
                    whiteSpace: 'nowrap',
                    color: 'var(--color-text-light)'
                  }}>
                    {new Date(day.date).toLocaleDateString('sv-SE', { 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '200px',
            color: 'var(--color-text-light)',
            flexDirection: 'column',
            gap: '10px'
          }}>
            <div style={{ fontSize: '48px' }}>📊</div>
            <div>Ingen trafikdata tillgänglig än</div>
            <div style={{ fontSize: '12px', textAlign: 'center' }}>
              Data kommer att visas när besökare interagerar med webbplatsen
            </div>
          </div>
        )}
      </ChartCard>
        </>
      )}

      {activeTab === 'products' && (
        <>
          <StatsGrid>
            <StatCard>
              <StatNumber>{formatNumber(totalProductViews)}</StatNumber>
              <StatLabel>Totala produktvisningar</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>{formatNumber(totalProductVisitors)}</StatNumber>
              <StatLabel>Unika produktbesökare</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>{formatNumber(averageProductViews || 0)}</StatNumber>
              <StatLabel>Snittvisningar per produkt</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>{productData.length}</StatNumber>
              <StatLabel>Produkter med trafik</StatLabel>
            </StatCard>
          </StatsGrid>

          {topProduct && (
            <ChartCard>
              <ChartTitle>🏅 Mest besökta produkt</ChartTitle>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                <div>
                  <h4 style={{ margin: 0 }}>{topProduct.name}</h4>
                  <p style={{ margin: '4px 0', color: '#6b7280' }}>Slug: {topProduct.slug}</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 600 }}>{formatNumber(topProduct.view_count)} visningar</div>
                  <div style={{ color: '#6b7280' }}>{formatNumber(topProduct.unique_visitors)} unika besökare</div>
                  <div style={{ color: '#6b7280' }}>{formatPrice(topProduct.price || 0)}</div>
                </div>
              </div>
            </ChartCard>
          )}

          <ChartCard>
            <ChartTitle>📦 Produktvisningar (topp {Math.min(productData.length, 50)})</ChartTitle>
            {productData.length > 0 ? (
              <Table>
                <thead>
                  <tr>
                    <Th>#</Th>
                    <Th>Produkt</Th>
                    <Th>Visningar</Th>
                    <Th>Unika besökare</Th>
                    <Th>Andel</Th>
                    <Th>Pris</Th>
                  </tr>
                </thead>
                <tbody>
                  {productData.map((product: any, index: number) => {
                    const share = totalProductViews > 0 ? (product.view_count / totalProductViews) * 100 : 0;
                    return (
                      <tr key={product.id || `${product.slug}-${index}`}>
                        <Td>{index + 1}</Td>
                        <Td>
                          <div style={{ fontWeight: 600 }}>{product.name}</div>
                          <div style={{ fontSize: '0.8em', color: '#6b7280' }}>{product.slug}</div>
                        </Td>
                        <Td>{formatNumber(product.view_count)}</Td>
                        <Td>{formatNumber(product.unique_visitors)}</Td>
                        <Td>{share.toFixed(1)}%</Td>
                        <Td>{formatPrice(product.price || 0)}</Td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                Inga produktvisningar registrerade än
              </div>
            )}
            {productPeriodDays && (
              <div style={{ marginTop: '12px', fontSize: '0.85em', color: '#6b7280' }}>
                Visar data för de senaste {productPeriodDays} dagarna.
              </div>
            )}
          </ChartCard>
        </>
      )}

      {activeTab === 'marketing' && (
        <>
          <StatsGrid>
            <StatCard>
              <StatNumber>{formatNumber(totalMarketingViews)}</StatNumber>
              <StatLabel>Totala sidvisningar</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>{formatNumber(totalMarketingVisitors)}</StatNumber>
              <StatLabel>Unika besökare</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>{formatDuration(avgMarketingTime || 0)}</StatNumber>
              <StatLabel>Genomsnittlig tid på sida</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>{marketingData.length}</StatNumber>
              <StatLabel>Aktiva marknadsföringssidor</StatLabel>
            </StatCard>
          </StatsGrid>

          {topMarketingPage && (
            <ChartCard>
              <ChartTitle>🌟 Mest besökta marknadsföringssidan</ChartTitle>
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
                <div>
                  <h4 style={{ margin: 0 }}>{topMarketingPage.title}</h4>
                  <p style={{ margin: '4px 0', color: '#6b7280' }}>/{topMarketingPage.slug}</p>
                  <a
                    href={`/${topMarketingPage.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: '0.9em', color: '#4338ca', textDecoration: 'underline' }}
                  >
                    Visa sida →
                  </a>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 600 }}>{formatNumber(topMarketingPage.view_count)} visningar</div>
                  <div style={{ color: '#6b7280' }}>{formatNumber(topMarketingPage.unique_visitors)} unika besökare</div>
                  <div style={{ color: '#6b7280' }}>
                    {formatDuration(topMarketingPage.avg_time_on_page || 0)} i snitt
                  </div>
                </div>
              </div>
            </ChartCard>
          )}

          <ChartCard>
            <ChartTitle>📣 Trafik till marknadsföringssidor</ChartTitle>
            {marketingData.length > 0 ? (
              <Table>
                <thead>
                  <tr>
                    <Th>#</Th>
                    <Th>Sida</Th>
                    <Th>Visningar</Th>
                    <Th>Unika</Th>
                    <Th>Andel</Th>
                    <Th>Tid på sida</Th>
                  </tr>
                </thead>
                <tbody>
                  {marketingData.map((page: any, index: number) => {
                    const share = page.view_share ?? (totalMarketingViews > 0 ? (page.view_count / totalMarketingViews) * 100 : 0);
                    return (
                      <tr key={page.id || page.slug}>
                        <Td>{index + 1}</Td>
                        <Td>
                          <div style={{ fontWeight: 600 }}>{page.title}</div>
                          <div style={{ fontSize: '0.8em', color: '#6b7280' }}>
                            <a
                              href={`/${page.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#4338ca' }}
                            >
                              /{page.slug}
                            </a>
                          </div>
                        </Td>
                        <Td>{formatNumber(page.view_count)}</Td>
                        <Td>{formatNumber(page.unique_visitors)}</Td>
                        <Td>{share.toFixed(1)}%</Td>
                        <Td>{formatDuration(page.avg_time_on_page || 0)}</Td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                Ingen trafik registrerad ännu
              </div>
            )}
            {marketingPeriodDays && (
              <div style={{ marginTop: '12px', fontSize: '0.85em', color: '#6b7280' }}>
                Visar data för de senaste {marketingPeriodDays} dagarna.
              </div>
            )}
          </ChartCard>
        </>
      )}

      {activeTab === 'bounce' && bounceAnalytics && (
        <>
          {/* Bounce Rate Overview */}
          <StatsGrid>
            <StatCard>
              <StatNumber>{bounceAnalytics.overview?.bounce_rate || 0}%</StatNumber>
              <StatLabel>Total Bounce Rate</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>{formatNumber(bounceAnalytics.overview?.bounce_sessions || 0)}</StatNumber>
              <StatLabel>Bounce-sessioner</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>{formatNumber(bounceAnalytics.overview?.total_sessions || 0)}</StatNumber>
              <StatLabel>Totala sessioner</StatLabel>
            </StatCard>
          </StatsGrid>

          {/* Exit Pages */}
          <BounceCard>
            <BounceTitle>
              🚪 Mest vanliga exit-sidor
              <BounceMetric>Här lämnar besökarna webbplatsen</BounceMetric>
            </BounceTitle>
            {bounceAnalytics.exit_pages?.length > 0 ? (
              <Table>
                <thead>
                  <tr>
                    <Th>Sida</Th>
                    <Th>Exit-antal</Th>
                    <Th>Exit-rate</Th>
                    <Th>Avg tid på sidan</Th>
                  </tr>
                </thead>
                <tbody>
                  {bounceAnalytics.exit_pages.slice(0, 10).map((page: any, index: number) => (
                    <tr key={index}>
                      <Td>
                        <strong>{page.page_title || page.page_url}</strong>
                        <div style={{ fontSize: '0.8em', color: '#666' }}>
                          {page.page_type}
                        </div>
                      </Td>
                      <Td>{formatNumber(page.exit_count)}</Td>
                      <Td>{page.exit_rate}%</Td>
                      <Td>{formatDuration(page.avg_time_on_page || 0)}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                Ingen exit-data tillgänglig än
              </div>
            )}
          </BounceCard>

          {/* Landing Page Bounces */}
          <BounceCard>
            <BounceTitle>
              📍 Bounce-rate per landningssida
              <BounceMetric>Vilka sidor får besökare att lämna direkt</BounceMetric>
            </BounceTitle>
            {bounceAnalytics.landing_pages?.length > 0 ? (
              <Table>
                <thead>
                  <tr>
                    <Th>Landningssida</Th>
                    <Th>Sessioner</Th>
                    <Th>Bounces</Th>
                    <Th>Bounce-rate</Th>
                  </tr>
                </thead>
                <tbody>
                  {bounceAnalytics.landing_pages.slice(0, 15).map((page: any, index: number) => (
                    <tr key={index}>
                      <Td>{page.landing_page}</Td>
                      <Td>{formatNumber(page.sessions)}</Td>
                      <Td>{formatNumber(page.bounces)}</Td>
                      <Td>
                        <strong style={{ color: page.bounce_rate > 70 ? '#ef4444' : page.bounce_rate > 40 ? '#f59e0b' : '#10b981' }}>
                          {page.bounce_rate}%
                        </strong>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                Ingen landningsside-data tillgänglig än
              </div>
            )}
          </BounceCard>

          {/* Shopping Funnel Analysis */}
          {bounceAnalytics.funnel?.length > 0 && (
            <BounceCard>
              <BounceTitle>
                🛍️ Shopping-tratt analys
                <BounceMetric>Var förlorar vi kunder i köpprocessen</BounceMetric>
              </BounceTitle>
              <FunnelContainer>
                {bounceAnalytics.funnel.map((step: any, index: number) => {
                  const maxSessions = Math.max(...bounceAnalytics.funnel.map((s: any) => s.unique_sessions));
                  const percentage = (step.unique_sessions / maxSessions) * 100;
                  
                  return (
                    <FunnelStep key={step.page_type} percentage={percentage}>
                      <div style={{ fontSize: '12px', textTransform: 'capitalize' }}>
                        {step.page_type === 'home' && '🏠 Hem'}
                        {step.page_type === 'category' && '📂 Kategori'}
                        {step.page_type === 'product' && '📦 Produkt'}
                        {step.page_type === 'checkout' && '💳 Kassa'}
                        {!['home', 'category', 'product', 'checkout'].includes(step.page_type) && step.page_type}
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                        {formatNumber(step.unique_sessions)}
                      </div>
                      <div style={{ fontSize: '10px', opacity: 0.9 }}>
                        {formatDuration(step.avg_time_on_page)}
                      </div>
                    </FunnelStep>
                  );
                })}
              </FunnelContainer>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
                * Visar unika sessioner som besökte varje sidtyp och genomsnittlig tid per sida
              </div>
            </BounceCard>
          )}

          {/* Device/Browser Bounce Rates */}
          {bounceAnalytics.device_bounce?.length > 0 && (
            <BounceCard>
              <BounceTitle>
                📱 Bounce-rate per enhet/webbläsare
                <BounceMetric>Tekniska problem som kan påverka bounce</BounceMetric>
              </BounceTitle>
              <Table>
                <thead>
                  <tr>
                    <Th>Enhet</Th>
                    <Th>Webbläsare</Th>
                    <Th>Sessioner</Th>
                    <Th>Bounce-rate</Th>
                  </tr>
                </thead>
                <tbody>
                  {bounceAnalytics.device_bounce.slice(0, 10).map((device: any, index: number) => (
                    <tr key={index}>
                      <Td style={{ textTransform: 'capitalize' }}>
                        {device.device_type === 'mobile' && '📱'} 
                        {device.device_type === 'desktop' && '💻'} 
                        {device.device_type === 'tablet' && '📱'} 
                        {device.device_type}
                      </Td>
                      <Td>{device.browser}</Td>
                      <Td>{formatNumber(device.sessions)}</Td>
                      <Td>
                        <strong style={{ color: device.bounce_rate > 70 ? '#ef4444' : device.bounce_rate > 40 ? '#f59e0b' : '#10b981' }}>
                          {device.bounce_rate}%
                        </strong>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </BounceCard>
          )}

          {/* Hourly Bounce Analysis */}
          {bounceAnalytics.hourly_bounce?.length > 0 && (
            <BounceCard>
              <BounceTitle>
                🕐 Bounce-rate per timme
                <BounceMetric>När på dagen bouncar besökare mest</BounceMetric>
              </BounceTitle>
              <div style={{ 
                display: 'flex', 
                alignItems: 'end', 
                gap: '4px', 
                height: '150px', 
                padding: '20px 0'
              }}>
                {bounceAnalytics.hourly_bounce.map((hour: any) => {
                  const maxBounceRate = Math.max(...bounceAnalytics.hourly_bounce.map((h: any) => h.bounce_rate));
                  const height = (hour.bounce_rate / maxBounceRate) * 100;
                  
                  return (
                    <div key={hour.hour} style={{ 
                      flex: 1,
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      position: 'relative'
                    }}>
                      <div style={{ 
                        fontSize: '10px', 
                        marginBottom: '5px',
                        fontWeight: '500'
                      }}>
                        {hour.bounce_rate}%
                      </div>
                      <div style={{
                        backgroundColor: hour.bounce_rate > 70 ? '#ef4444' : hour.bounce_rate > 40 ? '#f59e0b' : '#10b981',
                        height: `${Math.max(height, 5)}px`,
                        width: '100%',
                        borderRadius: '2px 2px 0 0',
                        minHeight: '5px'
                      }}
                      title={`Kl ${hour.hour}:00: ${hour.bounce_rate}% bounce-rate`}
                      />
                      <div style={{ 
                        fontSize: '10px',
                        marginTop: '5px',
                        color: 'var(--color-text-light)'
                      }}>
                        {hour.hour}:00
                      </div>
                    </div>
                  );
                })}
              </div>
            </BounceCard>
          )}
        </>
      )}

      {activeTab === 'bounce' && !bounceAnalytics && !loading && (
        <BounceCard>
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📈</div>
            <div style={{ fontSize: '18px', marginBottom: '8px' }}>Ingen bounce-data tillgänglig</div>
            <div style={{ fontSize: '14px' }}>
              Data kommer att visas när vi har tillräckligt med besökare och interaktioner
            </div>
          </div>
        </BounceCard>
      )}
    </VisitorsWrapper>
  );
};

export default Visitors;
